/**
 * Rubric/coaching formatting helpers — extracted verbatim out of `tools/smart-capture.ts`
 * (STD-2 / DEC-1504 shrink-only: that file is grandfathered over-budget and may not grow) to
 * pay for this review round's additions elsewhere in that file (the round-3 fixes: threading
 * `autoCommit` through the batch preview next-action, the single-preview output schema member,
 * and the batch-local duplicate-name check).
 *
 * Pure, self-contained, and behaviour-unchanged by the move — re-exported from
 * smart-capture.ts (and imported there for its own internal call sites) so every existing
 * importer (`tools/knowledge.ts`, `tools/quality.ts`, test files importing from
 * `../tools/smart-capture.js`) keeps working unmodified, the same pattern already used for
 * `batchCaptureOutputSchema` (lib/batchCaptureOutput.ts).
 *
 * `withTimeout` has no remaining call site anywhere in this repo (verified by grep before the
 * move) — moved rather than deleted so this extraction stays a pure relocation with zero
 * behavioral risk; a follow-up cleanup can remove it separately if still dead.
 */

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error("quality coaching timeout")), ms),
    ),
  ]);
}

/**
 * WP-480 S1 (FEAT-1358): a short capture-confirmation phrase derived from the
 * SERVER rubric verdict — replaces the deleted client `Quality N/10` recompute
 * (single scorer; DEC-1418 the connector owns the sentence). Passive/absent
 * verdicts (no rubric criteria) read as "not evaluated"; otherwise it reports the
 * rubric criteria pass count and points at the coaching already in the response.
 */
export function verdictQualityPhrase(
  result: { verdict?: { tier?: string; passed?: boolean; criteria?: Array<{ passed: boolean }> } } | null | undefined,
): string {
  const v = result?.verdict;
  const criteria = v?.criteria ?? [];
  // "No rubric" is decided by whether the verdict HAS criteria — never by its tier.
  // Passive-tier types (tension, glossary) carry real rubric criteria, and for them the
  // heuristic IS the full judgment (the background LLM pass deliberately never runs).
  // Short-circuiting on tier === 'passive' therefore threw away a real verdict and told
  // the agent "not evaluated" while qualityVerdict recorded failing criteria.
  if (!v || criteria.length === 0) {
    return "Quality: not evaluated (no rubric for this type).";
  }
  const total = criteria.length;
  const passed = criteria.filter((c) => c.passed).length;
  return v.passed
    ? `Quality: all ${total} rubric criteria pass.`
    : `Quality: ${passed}/${total} rubric criteria pass — see the coaching above.`;
}

/**
 * Format a full quality coaching result (from evaluateAtCapture / evaluateAtCommit)
 * for inclusion in MCP capture/commit responses.
 */
export function formatRubricCoaching(result: any): string {
  const { verdict, rogerMartin } = result;
  if (!verdict || verdict.criteria.length === 0) return "";

  const lines: string[] = ["## Semantic Quality"];
  const failed = (verdict.criteria ?? []).filter((c: any) => !c.passed);
  const total = verdict.criteria?.length ?? 0;
  const passedCount = total - failed.length;

  if (verdict.passed) {
    lines.push(`All ${total} rubric criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier).`);
  } else {
    lines.push(`${passedCount}/${total} criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier)`);
    lines.push("");
    for (const c of verdict.criteria) {
      const icon = c.passed ? "[x]" : "[ ]";
      const extra = c.passed ? "" : ` — ${c.hint}`;
      lines.push(`${icon} ${c.id}${extra}`);
    }

    if (verdict.weakest) {
      lines.push("");
      lines.push(`**Coaching hint:** ${verdict.weakest.hint}`);
      lines.push(`_Question to consider:_ ${verdict.weakest.questionTemplate}`);
    }
  }

  if (rogerMartin) {
    lines.push("");
    lines.push("### Roger Martin Test");
    if (rogerMartin.isStrategicChoice) {
      lines.push("This principle passes — the opposite is a reasonable strategic choice.");
    } else {
      lines.push(`This principle may not be a strategic choice. ${rogerMartin.reasoning}`);
      if (rogerMartin.suggestion) {
        lines.push(`_Suggestion:_ ${rogerMartin.suggestion}`);
      }
    }
  }

  return lines.join("\n");
}

/**
 * Format a cached rubric verdict section for quality check responses.
 * SOS-e4r5cn: capability and visibility must ship together.
 */
export function formatRubricVerdictSection(verdict: any): string {
  if (!verdict || !verdict.criteria || verdict.criteria.length === 0) return "";

  const lines: string[] = ["## Semantic Quality"];
  const failed = verdict.criteria.filter((c: any) => !c.passed);
  const total = verdict.criteria.length;
  const passedCount = total - failed.length;

  const durationSuffix = verdict.llmDurationMs ? ` in ${(verdict.llmDurationMs / 1000).toFixed(1)}s` : '';
  const statusNote = verdict.llmStatus === 'pending'
    ? ' — LLM evaluation in progress...'
    : verdict.llmStatus === 'failed'
      ? ` — LLM evaluation failed${verdict.llmError ? `: ${verdict.llmError}` : ''}, showing heuristic results`
      : verdict.source === 'llm' && durationSuffix
        ? ` — evaluated${durationSuffix}`
        : '';

  if (failed.length === 0) {
    lines.push(`All ${total} rubric criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier, ${verdict.source} evaluation).${statusNote}`);
  } else {
    lines.push(`${passedCount}/${total} criteria pass for \`${verdict.canonicalKey}\` (${verdict.tier} tier, ${verdict.source} evaluation)${statusNote}`);
    lines.push("");
    for (const c of verdict.criteria) {
      const icon = c.passed ? "[x]" : "[ ]";
      const extra = c.passed ? "" : ` — ${c.hint}`;
      lines.push(`${icon} ${c.id}${extra}`);
    }

    if (verdict.weakest) {
      lines.push("");
      lines.push(`**Top improvement:** ${verdict.weakest.hint}`);
    }
  }

  if (verdict.rogerMartin) {
    const rm = verdict.rogerMartin;
    lines.push("");
    lines.push("### Roger Martin Test");
    if (rm.isStrategicChoice) {
      lines.push(`This is a real strategic choice — the opposite is reasonable. ${rm.reasoning}`);
    } else {
      lines.push(`This may not be a strategic choice. ${rm.reasoning}`);
      if (rm.suggestion) {
        lines.push(`_Suggestion:_ ${rm.suggestion}`);
      }
    }
  }

  return lines.join("\n");
}
